'use client'

import HeaderGap from '@/components/atoms/headerGap'
import Container from '@/components/templates/container'
import Layout from '@/components/templates/layout'
import titleCase from '@/utils/titleCase'
import { Photo, Coaches, body, h2, solidBtn, outlineBtn, ext } from './index'

const WHATSAPP = 'https://wa.me/6281337582672'
const label = 'font-chivoMono text-[11px] uppercase tracking-widest text-khaki'

// ── Classes / sessions ─────────────────────────────────────────────────────

const ClassCard = ({ title, duration, price, description, url, urlLabel }) => (
  <a href={url || WHATSAPP} {...ext(url || WHATSAPP)}
    className="group flex flex-col gap-3 p-5 lg:p-6 border border-dune hover:border-khaki transition-colors duration-200">
    <h3 className="font-ttDrugsTrial text-olive text-[20px] lg:text-[22px] leading-tight">{titleCase(title)}</h3>
    {(duration || price) && (
      <span className="font-chivoMono text-[12px] uppercase tracking-widest text-khaki">
        {[duration, price].filter(Boolean).join(' · ')}
      </span>
    )}
    {description && <p className="font-chivoMono text-[13px] text-umber">{description}</p>}
    <span className="mt-auto pt-2 font-chivoMono text-[12px] uppercase tracking-widest text-olive transition-transform duration-200 group-hover:translate-x-1">
      {urlLabel || 'Book'} <span aria-hidden="true">→</span>
    </span>
  </a>
)

const Classes = ({ heading, classes, image, imageAlt }) => {
  if (!classes?.length) return null
  const hasImage = !!image?.asset
  return (
    <Container outerClassName="mt-[100px] lg:mt-[150px]">
      <section id="classes" className="scroll-mt-[100px]">
        <h2 className={`${h2} mb-8 lg:mb-10`}>{titleCase(heading)}</h2>
        <div className={`grid grid-cols-1 gap-3 lg:gap-4 ${hasImage ? 'lg:grid-cols-[2fr_3fr] items-stretch' : ''}`}>
          {hasImage && <Photo image={image} alt={imageAlt} className="hidden lg:block h-full min-h-[360px]" />}
          <div className={`grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4 ${hasImage ? '' : 'lg:grid-cols-3'}`}>
            {classes.map((c, i) => <ClassCard key={i} {...c} />)}
          </div>
        </div>
      </section>
    </Container>
  )
}

// ── Coaches ────────────────────────────────────────────────────────────────

// ── Page ───────────────────────────────────────────────────────────────────

// children: extra sections shown between the coaches and the closing call to action
const CoachingPage = ({
  footerAPI, settingAPI,
  eyebrow, heading, intros = [],
  classesHeading, classes, classesImage, classesImageAlt,
  coachesHeading, coaches = [], focusLabel = 'Coaching focuses on',
  bookUrl, bookLabel = 'Book on Playtomic',
  cta = {},
  children,
}) => (
  <Layout settingAPI={settingAPI} footerAPI={footerAPI}>
    <HeaderGap />

    {/* Header */}
    <Container outerClassName="mt-[60px] lg:mt-[100px]">
      <div className="flex flex-col items-center text-center gap-6">
        {eyebrow && <span className={label}>{eyebrow}</span>}
        <h1 className="font-ttDrugsTrial text-olive text-[40px] md:text-[56px] leading-none max-w-[900px]">{titleCase(heading)}</h1>
        {intros.filter(Boolean).map((p, i) => <p key={i} className={`${body} max-w-[680px]`}>{p}</p>)}
        <div className="flex flex-wrap justify-center gap-3 mt-2">
          <a href={bookUrl} {...ext(bookUrl)} className={solidBtn}>{bookLabel}</a>
          <a href={WHATSAPP} {...ext(WHATSAPP)} className={outlineBtn}>Book on WhatsApp</a>
        </div>
      </div>
    </Container>

    <Classes heading={classesHeading} classes={classes} image={classesImage} imageAlt={classesImageAlt} />

    <Coaches
      heading={coachesHeading || (coaches.length > 1 ? 'Your Coaches' : 'Your Coach')}
      coaches={coaches}
      focusLabel={focusLabel}
      bookUrl={bookUrl}
    />

    {children}

    {/* Closing call to action */}
    <section className="w-full bg-forest text-background px-smallMobile md:px-mediumDesktop py-[90px] lg:py-[120px] mt-[100px] lg:mt-[150px]">
      <div className="max-w-container mx-auto flex flex-col items-center text-center gap-6">
        <h2 className="font-ttDrugsTrial text-[30px] md:text-[40px] leading-none">{titleCase(cta.heading || 'Ready to Improve Your Game?')}</h2>
        {cta.description && <p className="font-chivoMono text-bodyMobile md:text-bodyDesktop text-background/75 max-w-[560px]">{cta.description}</p>}
        <div className="flex flex-wrap justify-center gap-3 mt-2">
          <a href={cta.url || bookUrl} {...ext(cta.url || bookUrl)}
            className="px-7 py-4 bg-dune text-forest font-chivoMono text-[13px] uppercase tracking-widest hover:bg-background transition-colors duration-300">
            {cta.label || bookLabel}
          </a>
          <a href={WHATSAPP} {...ext(WHATSAPP)}
            className="px-7 py-4 border border-background/50 text-background font-chivoMono text-[13px] uppercase tracking-widest hover:border-background transition-colors duration-300">
            Book on WhatsApp
          </a>
        </div>
      </div>
    </section>
  </Layout>
)

export default CoachingPage
